/**
 * Display labels for reference lists whose NAMES are not unique.
 *
 * A pipeline stores topics by `topic.key` (`UNIQUE (key)`) and templates by
 * `reply_template.id`; neither table makes the human name unique, so two
 * topics or two templates may well be called the same. Identity therefore
 * always travels as the key/id, and the name is only a caption — but a caption
 * shared by two options makes them indistinguishable to the operator (and any
 * name → id lookup picks one of them arbitrarily). Every place that SHOWS a
 * topic or template takes its text from here: a unique name is shown as is, a
 * name shared with another entry (case- and edge-space-insensitively) gets its
 * identity appended — «Возврат · refund», «Отказ · 3f9c1a2b».
 */
/** Key of name comparison: «Возврат» and « возврат » read as the same caption. */
const normName = (name) => name.trim().toLocaleLowerCase('ru-RU');
/**
 * id → label for `items`. `suffixOf` is the short identity appended to a
 * shared (or empty) name; an id that is not in `items` is not in the map —
 * callers fall back to the raw id, which is what an unknown reference is.
 */
export function disambiguatedLabels(items, idOf, nameOf, suffixOf) {
    const count = new Map();
    for (const item of items) {
        const n = normName(nameOf(item));
        count.set(n, (count.get(n) ?? 0) + 1);
    }
    const out = new Map();
    for (const item of items) {
        const name = nameOf(item).trim();
        const shared = name === '' || (count.get(normName(name)) ?? 0) > 1;
        out.set(idOf(item), shared ? (name === '' ? suffixOf(item) : `${name} · ${suffixOf(item)}`) : name);
    }
    return out;
}
/** Topic key → label; a shared name is told apart by the key. */
export const topicLabels = (topics) => disambiguatedLabels(topics, (t) => t.key, (t) => t.name, (t) => t.key);
/** Template id → label; a shared name is told apart by the id's first 8 characters. */
export const templateLabels = (templates) => disambiguatedLabels(templates, (t) => t.id, (t) => t.name, (t) => t.id.slice(0, 8));
//# sourceMappingURL=labels.js.map